// ── VerifierModals ────────────────────────────────────────────────────────────
// H-6: Modals for verifier actions on submitted assignments.
// - ApproveModal: picks final outcome from proposed or overrides; fires submitted → terminal
// - SentBackModal: requires a comment; fires submitted → sent_back

import { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTransitionAssignment } from './api';
import type { Assignment, AssignmentState, ProposedOutcome } from '@grc/shared';

// ── Approve Modal ─────────────────────────────────────────────────────────────

const TERMINAL_OPTIONS: { value: ProposedOutcome; label: string }[] = [
  { value: 'implemented',           label: 'Implemented — control is in place' },
  { value: 'compensating_control',  label: 'Compensating control — alternative mitigation accepted' },
  { value: 'not_applicable',        label: 'Not applicable — control is out of scope' },
];

interface ApproveModalProps {
  open: boolean;
  onClose: () => void;
  assignment: Assignment;
  controlId: string;
}

export function ApproveModal({ open, onClose, assignment, controlId }: ApproveModalProps) {
  const transitionMutation = useTransitionAssignment(controlId);
  const defaultOutcome: ProposedOutcome = (assignment.proposed_outcome as ProposedOutcome) ?? 'implemented';
  const [outcome, setOutcome] = useState<ProposedOutcome>(defaultOutcome);

  function handleConfirm() {
    transitionMutation.mutate(
      { id: assignment.id, to: outcome as AssignmentState },
      {
        onSuccess: () => {
          toast.success('Control approved');
          onClose();
        },
        onError: (err: unknown) => {
          const msg = (err as { response?: { data?: { errors?: { message?: string }[] } } })
            ?.response?.data?.errors?.[0]?.message ?? 'Transition failed';
          toast.error(msg);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">Approve control</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {assignment.proposed_outcome && (
            <div className="rounded-md border border-accent-sky/30 bg-accent-sky/5 px-3 py-2 text-xs text-text-secondary">
              Assignee suggested:{' '}
              <span className="font-medium text-text-primary">
                {TERMINAL_OPTIONS.find((o) => o.value === assignment.proposed_outcome)?.label ?? assignment.proposed_outcome}
              </span>
            </div>
          )}

          <div>
            <label className="text-[10px] font-semibold text-text-secondary uppercase tracking-wider mb-1.5 block">
              Final outcome
            </label>
            <select
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as ProposedOutcome)}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-sky/50"
            >
              {TERMINAL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-border-subtle">
          <Button variant="ghost" size="sm" className="text-xs" onClick={onClose} disabled={transitionMutation.isPending}>
            Cancel
          </Button>
          <Button variant="default" size="sm" className="text-xs" onClick={handleConfirm} disabled={transitionMutation.isPending}>
            Confirm approval
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Sent Back Modal ───────────────────────────────────────────────────────────

interface SentBackModalProps {
  open: boolean;
  onClose: () => void;
  assignment: Assignment;
  controlId: string;
}

export function SentBackModal({ open, onClose, assignment, controlId }: SentBackModalProps) {
  const transitionMutation = useTransitionAssignment(controlId);
  const [comment, setComment] = useState('');

  const isValid = comment.trim().length > 0;

  function handleConfirm() {
    if (!isValid) return;
    transitionMutation.mutate(
      { id: assignment.id, to: 'sent_back', comment: comment.trim() },
      {
        onSuccess: () => {
          toast.success('Assignment sent back');
          setComment('');
          onClose();
        },
        onError: (err: unknown) => {
          const msg = (err as { response?: { data?: { errors?: { message?: string }[] } } })
            ?.response?.data?.errors?.[0]?.message ?? 'Transition failed';
          toast.error(msg);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setComment(''); onClose(); } }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">Send back to assignee</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-xs text-text-secondary">
            Explain what needs to be addressed. The assignee will see this comment.
          </p>
          <div>
            <label className="text-[10px] font-semibold text-text-secondary uppercase tracking-wider mb-1.5 block">
              Comment <span className="text-accent-rose">*</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="e.g. Evidence is missing a date range. Please re-upload with Q1 2026 scope clearly stated."
              rows={4}
              maxLength={5000}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-1 focus:ring-accent-sky/50 resize-none"
            />
            <p className="text-[10px] text-text-secondary mt-0.5">{comment.length}/5000</p>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-border-subtle">
          <Button variant="ghost" size="sm" className="text-xs" onClick={() => { setComment(''); onClose(); }} disabled={transitionMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            className="text-xs"
            onClick={handleConfirm}
            disabled={!isValid || transitionMutation.isPending}
          >
            Send back
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
